// The tile pictures on the page (design 11 §4, §5).
//
// The faces come pre-rendered: scripts/atlas.mjs draws every face of src/assets/tiles on its blank front, the
// back teal, the white dragon framed and the red fives marked, into one WebP sheet per face width (64, 128,
// 192 px). No vector art is fetched or rasterised at run time. The page's own small tiles show their face as
// a CSS background cut from a sheet; Vite hashes the sheets' names, so Caddy caches them for a year.
//
// The table's tiles (design 10 §3) are sprites: a finished picture of the whole tile, its sides and contact
// shadow drawn in (ui/spriteArt), so moving a tile repaints nothing. A worker (ui/sprites.worker) draws each
// family's sprites into one sheet at the size the table shows them, off the page's thread, and hands it over
// as one image; each tile is an element showing its region of it (ui/TileLayer), as the page's small tiles
// show theirs, so the browser decodes it once, off the main thread, and moving a tile repaints nothing. (One
// canvas per tile, tried first, made every commit of the page's layers cost a slow phone ~180 ms.) The worker
// keeps finished sheets between visits, and the page remembers the sizes and angle it last used on this
// screen, so a later visit asks for them as its scripts start and has them before the board is drawn.

import { createSignal } from "solid-js";
import manifest from "../assets/atlas/faces.json";
import sheet64 from "../assets/atlas/faces-64.webp?url";
import sheet96 from "../assets/atlas/faces-96.webp?url";
import sheet128 from "../assets/atlas/faces-128.webp?url";
import sheet192 from "../assets/atlas/faces-192.webp?url";
import { faceSizes } from "./faceSheet";
import { LOOKS, SHEET_CACHE, SHEET_WIDTHS, cell, drawSheet, layout, loadFaces, sheetKey } from "./spriteArt";
import type { Faces, Family, Item, Kind, Look } from "./spriteArt";
import type { DrawFailure, DrawReply, DrawRequest } from "./sprites.worker";

export { BACK, spriteBox, topFace } from "./spriteArt";
export type { Box, Kind, Look } from "./spriteArt";

const SHEET_URLS: Record<number, string> = { 64: sheet64, 96: sheet96, 128: sheet128, 192: sheet192 };
/** The smallest face sheet whose faces are at least `px` wide (device pixels), else the largest. */
const sheetFor = (px: number) => SHEET_WIDTHS.find((w) => w >= px) ?? SHEET_WIDTHS[SHEET_WIDTHS.length - 1]!;

const browser = typeof window !== "undefined";
const dpr = () => (browser ? window.devicePixelRatio || 1 : 1);

// --- the page's own small tiles ---------------------------------------------------------------------

/** The face sheet this screen loads, for everything: the one the HTML's preload chose (vite.config.ts), else
 *  the same choice made here (ui/faceSheet). */
function chooseFaces(): number {
  if (!browser) return SHEET_WIDTHS[1]!;
  try {
    const loaded = new Set(performance.getEntriesByType("resource").map((r) => new URL(r.name).pathname));
    const preloaded = SHEET_WIDTHS.filter((w) => loaded.has(new URL(SHEET_URLS[w]!, location.href).pathname));
    if (preloaded.length) return preloaded[preloaded.length - 1]!;
  } catch {
    // no resource timing: chosen below
  }
  const need = faceSizes() * dpr();
  return SHEET_WIDTHS.find((w) => manifest.sheets[String(w) as keyof typeof manifest.sheets].width >= need) ?? SHEET_WIDTHS[SHEET_WIDTHS.length - 1]!;
}
const FACES = chooseFaces();
/** The face sheet the table's sprites at `px` device pixels a tile are drawn from: the loaded one, unless the
 *  table shows its tiles larger than it draws well. */
const facesFor = (px: number) => (FACES >= px * 0.9 ? FACES : sheetFor(px));
const small = FACES;

/** The CSS background of tile `name` (a file name, or BACK): its face cut from a sheet. */
export function tileArt(name: string): { "background-image": string; "background-size": string; "background-position": string } {
  const s = manifest.sheets[String(small) as keyof typeof manifest.sheets];
  const c = cell(small, name);
  const pos = (at: number, size: number, whole: number) => `${((at / (whole - size)) * 100).toFixed(4)}%`;
  return {
    "background-image": `url(${SHEET_URLS[small]})`,
    "background-size": `${((s.width / c.w) * 100).toFixed(4)}% ${((s.height / c.h) * 100).toFixed(4)}%`,
    "background-position": `${pos(c.x, c.w, s.width)} ${pos(c.y, c.h, s.height)}`,
  };
}

// --- the table's sprite sheets ----------------------------------------------------------------------

/** A sprite: its sheet (an image the page holds as a blob: URL) and its rectangle there. */
export interface Sprite {
  key: string; // unique for this picture: the sheet's and the sprite's
  url: string;
  x: number;
  y: number;
  w: number;
  h: number;
  sheetW: number; // the sheet's size, in pixels
  sheetH: number;
}

interface Sheet {
  id: number;
  url: string;
  width: number;
  height: number;
  at: Map<string, Item>;
  w: number; // a tile's width in it, in device pixels
  deg: number;
  faces: number; // the face sheet it was drawn from
}

const sheets: Partial<Record<Family, Sheet>> = {};
const asked: Partial<Record<Family, { id: number; w: number; deg: number; faces: number }>> = {};
const arrived: Partial<Record<Family, Sheet>> = {}; // drawn, waiting for the other family's to show together
// Changes when a sheet is shown: what reads it (the tiles, the board's reveal) follows.
const [version, setVersion] = createSignal(0);
let wanted = 20; // the angle the table is asked to be seen at
let sizes: { hand: number; table: number } | null = null;

/** The sheets shown: read it to follow them (a Solid signal). */
export const artVersion = version;

/** The angle the table's sprites are drawn for, which the board leans by with them (ui/Table); until any
 *  are, the one asked for. */
export const tableAngle = () => sheets.table?.deg ?? wanted;

/** Whether every sheet asked for is shown: false while one is on its way (from an earlier visit's cache at
 *  startup, or being drawn). */
export const sheetsSettled = () => !(["hand", "table"] as const).some((f) => asked[f] && sheets[f]?.id !== asked[f]!.id);

/** The sprite of tile file `name` (or BACK) as `kind` in orientation `o`, or null until its sheet is ready. */
export function sprite(kind: Kind, name: string, o: number): Sprite | null {
  const s = sheets[kind === "hand" ? "hand" : "table"];
  const it = s?.at.get(`${kind}:${name}:${o}`);
  if (!s || !it) return null;
  return { key: `${s.id}:${it[0]}`, url: s.url, x: it[1], y: it[2], w: it[3], h: it[4], sheetW: s.width, sheetH: s.height };
}

/** The CSS showing sprite `s` in `look` on an element of any size: its region of the sheet, scaled to fill
 *  the element, darkened by a filter the compositor applies (no repaint). */
export function spriteStyle(s: Sprite, look: Look): { "background-image": string; "background-size": string; "background-position": string; filter: string } {
  const pos = (at: number, size: number, whole: number) => (whole > size ? `${((at / (whole - size)) * 100).toFixed(4)}%` : "0%");
  return {
    "background-image": `url(${s.url})`,
    "background-size": `${((s.sheetW / s.w) * 100).toFixed(4)}% ${((s.sheetH / s.h) * 100).toFixed(4)}%`,
    "background-position": `${pos(s.x, s.w, s.sheetW)} ${pos(s.y, s.h, s.sheetH)}`,
    filter: LOOKS[look] ? `brightness(${(1 - LOOKS[look]).toFixed(2)})` : "",
  };
}

// The worker, where it can draw; else the page draws the sheets itself (design 11 §5, the fallback).
let worker: Worker | null = null;
const replies = new Map<number, (r: DrawReply) => void>();
if (browser && typeof Worker === "function" && typeof OffscreenCanvas === "function") {
  try {
    worker = new Worker(new URL("./sprites.worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (e: MessageEvent<DrawReply | DrawFailure>) => {
      const d = e.data;
      const reply = replies.get(d.id);
      replies.delete(d.id);
      if ("failed" in d) throw new Error(d.failed); // to the page's error handlers (main.tsx: a sheet gone reloads)
      reply?.(d);
    };
    worker.onerror = () => (worker = null);

  } catch {
    worker = null;
  }
}

// The worker fetches the face sheet now and decodes it on its own thread, so a first visit draws as soon as the
// table measures itself. The HTML's preload has already asked for the same file: the browser's cache makes
// this request wait for that one instead of downloading it again.
if (worker) worker.postMessage({ id: 0, warm: true, sheetUrl: SHEET_URLS[FACES]!, sheetW: FACES } satisfies Partial<DrawRequest>);

let localFaces: Promise<Faces> | null = null;
let localW = 0;
async function drawHere(r: DrawRequest): Promise<DrawReply> {
  if (!localFaces || localW !== r.sheetW) {
    localW = r.sheetW;
    localFaces = loadFaces(r.sheetUrl, r.sheetW);
  }
  const f = await localFaces;
  const { width, height, items } = layout(r.family, r.w, r.deg);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (ctx) drawSheet(ctx, f, r.w, r.deg, items);
  const blob = ctx ? await new Promise<Blob | null>((ok) => canvas.toBlob(ok)) : null;
  return { id: r.id, blob, width, height, items, cached: false };
}

/** Shows the sheets that have arrived, both families in one render when both were asked for together. */
function show(): void {
  if ((["hand", "table"] as const).some((f) => asked[f] && !arrived[f] && sheets[f]?.id !== asked[f]!.id)) return;
  let changed = false;
  for (const f of ["hand", "table"] as const) {
    const s = arrived[f];
    if (!s) continue;
    const old = sheets[f];
    sheets[f] = s;
    delete arrived[f];
    changed = true;
    // A tile still in flight may show the old sheet for a moment.
    if (old) setTimeout(() => URL.revokeObjectURL(old.url), 5000);
  }
  if (!changed) return;
  setVersion((n) => n + 1);
}

async function fromCache(family: Family, w: number, deg: number, faces: number): Promise<Blob | null> {
  try {
    const hit = await (await caches.open(SHEET_CACHE)).match(sheetKey(location.origin, family, w, deg, faces));
    return hit ? await hit.blob() : null;
  } catch {
    return null; // no Cache Storage: the worker draws it
  }
}

let nextId = 1;
/** Asks for a family's sheet at width `w` and angle `deg`, unless it is drawn or asked for already. */
function ask(family: Family, w: number, deg: number): void {
  const faces = facesFor(sizes?.hand ?? w);
  const have = asked[family];
  if (have && have.w === w && have.deg === deg && have.faces === faces) return;
  const id = nextId++;
  asked[family] = { id, w, deg, faces };
  delete arrived[family];
  const req: DrawRequest = { id, family, w, deg, sheetUrl: SHEET_URLS[faces]!, sheetW: faces };
  const done = (r: DrawReply) => {
    if (asked[family]?.id !== id || !r.blob) return; // a newer one was asked for
    // Shown at once: the browser decodes the sheet on its drawing threads, before the frame that shows it.
    const url = URL.createObjectURL(r.blob);
    arrived[family] = { id, url, width: r.width, height: r.height, at: new Map(r.items.map((it) => [it[0], it])), w, deg, faces };
    show();
  };
  const draw = () => {
    if (worker) {
      replies.set(id, done);
      worker.postMessage(req);
    } else void drawHere(req).then(done);
  };
  // A sheet kept from an earlier visit is read here at once, without waiting for the worker to start.
  void fromCache(family, w, deg, faces).then((blob) => {
    if (!blob) return draw();
    const { width, height, items } = layout(family, w, deg);
    done({ id, blob, width, height, items, cached: true });
  });
}

const far = (a: number, b: number | undefined) => !b || Math.abs(a - b) > 0.15 * a;

function request(): void {
  if (!sizes) return;
  const t = asked.table;
  const h = asked.hand;
  if (far(sizes.hand, h?.w) || facesFor(sizes.hand) > (h?.faces ?? 0)) ask("hand", sizes.hand, 0);
  if (far(sizes.table, t?.w) || t?.deg !== wanted || facesFor(sizes.hand) > (t?.faces ?? 0)) ask("table", sizes.table, wanted);
  remember();
}

// The sizes and angle last used on this screen, so a later visit asks for its sheets as its scripts start.
const MEMORY = "mj:sprite-sizes";
const screenKey = () => (browser ? `${innerWidth}x${innerHeight}@${dpr()}` : "");
function remember(): void {
  if (!sizes) return;
  try {
    const all = JSON.parse(localStorage.getItem(MEMORY) ?? "{}") as Record<string, unknown>;
    delete all[screenKey()];
    const kept = Object.entries(all).slice(-3);
    localStorage.setItem(MEMORY, JSON.stringify(Object.fromEntries([...kept, [screenKey(), { ...sizes, deg: wanted }]])));
  } catch {
    // no storage: drawn when the table measures itself
  }
}
/** The sizes the table will show its tiles at on this screen, read from the stylesheet before the table is
 *  drawn: a hidden copy of its frame with a tile of each kind (the board at its default layout). */
function predicted(): { hand: number; table: number } | null {
  const play = document.createElement("main");
  play.className = "play";
  play.setAttribute("aria-hidden", "true");
  play.style.cssText = "position: fixed; inset: 0; visibility: hidden; pointer-events: none; z-index: -1";
  play.innerHTML = '<div class="table"><div class="board view-top"><span class="tile tile-s"></span></div><div class="own"><div class="own-hand"><span class="tile tile-l"></span></div></div></div>';
  document.body.append(play);
  try {
    const t = (play.querySelector(".tile-s") as HTMLElement).offsetWidth;
    const T = (play.querySelector(".tile-l") as HTMLElement).offsetWidth;
    return t > 0 && T > 0 ? { hand: T, table: t } : null;
  } finally {
    play.remove();
  }
}

// As the scripts start, the sheets this screen will need are asked for: the sizes and angle remembered from an
// earlier visit (read from the cache at once), else the sizes the stylesheet gives (drawn in the worker as soon
// as the face sheet is in, while the rest of the page still loads).
if (browser) {
  try {
    const m = (JSON.parse(localStorage.getItem(MEMORY) ?? "{}") as Record<string, { hand: number; table: number; deg: number }>)[screenKey()];
    if (m && m.hand > 0 && m.table > 0) {
      wanted = m.deg;
      sizes = { hand: m.hand, table: m.table };
    }
  } catch {
    // nothing remembered
  }
  // The board's lean before anything is laid out (ui/Table sets the same): set after the table's first
  // measure, it resized the board and every tile was placed twice.
  document.documentElement.style.setProperty("--lean", `${wanted}deg`);
  if (!sizes) {
    try {
      const p = predicted();
      if (p) setSizes(p.hand, p.table);
    } catch {
      // measured when the table is drawn
    }
  } else request();
}

/** Asks for the table seen `deg` from straight above: its sprites are drawn again and replace the old ones
 *  all at once, the board leaning with them. */
let pending: ReturnType<typeof setTimeout> | null = null;
export function setAngle(deg: number): void {
  if (deg === wanted) return;
  wanted = deg;
  if (pending) clearTimeout(pending);
  // A slider sends many angles: only the one it rests on is drawn. The first is drawn at once.
  if (!sheets.table) request();
  else
    pending = setTimeout(() => {
      pending = null;
      request();
    }, 250);
}

/** The sizes the table shows its tiles at, in CSS pixels: the own row's and the board's. The sprites are
 *  drawn at those sizes on the screen's density (a little over, for a tile lifted in flight), never larger,
 *  and again when a size changes by more than 15% (a phone turned on its side). */
export function setSizes(hand: number, table: number): void {
  const px = (css: number) => Math.min(256, Math.max(24, Math.ceil(css * dpr() * 1.1)));
  const next = { hand: px(hand), table: px(table) };
  if (sizes && !far(next.hand, sizes.hand) && !far(next.table, sizes.table)) return;
  sizes = next;
  request();
}
